import React, { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, Chip, IconButton, Typography } from "@mui/material";
import { useTablePagination, useAppMeta, useAuth } from "hooks";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import ReactHtmlParser from "html-react-parser";
import AppTable from "components/Shared/AppTable";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import TableFilter from "components/Shared/AppTable/TableFilter";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import { BaseTabs } from "components/Shared/BaseTabs";
import HighlightedText from "components/Shared/HighlightedText";
import { useLazyGetFilteredShipmentActionsQuery } from "store/services/shipmentActionApi";
import { useLazyGetDDLBranchListQuery } from "store/services/branchApi";
import { useLazyGetDDLUserListQuery } from "store/services/userApi";
import { useLazyGetDdlVendorProfileQuery } from "store/services/vendorProfileApi";
import { Add } from "@mui/icons-material";

const ShipmentActionFollowupList = () => {
  const [searchParams] = useSearchParams();
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const { isVendor, viewEntity } = useAuth();

  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  // State for filters
  const [user, setUser] = React.useState("");
  const [vendor, setVendor] = React.useState("");
  const [systemType, setSystemType] = React.useState("");

  const breadcrumbs = [
    {
      title: "Reports",
      path: "/app/crm/reports/list",
    },
    {
      title: "Shipment Actions",
    },
  ];

  const ActionListTabs = [
    { label: "Active", value: "Active" },
    { label: "Closed", value: "Closed" },
  ];

  const systemTypeOptions = [
    { label: "All System Types", value: "" },
    { label: "Internal", value: "Internal" },
    { label: "External", value: "External" },
  ];
  const columns = [
    {
      name: "ID",
      selector: (row) => row?.shipment?.shipmentId || row?.shipmentId,
      cell: (row) => {
        const displayId = row?.shipment?.shipmentId || row?.shipmentId;
        const path = isVendor
          ? `/app/vendor/shipment/${row?.shipment?._id || row?.shipmentId
          }/detail?tab=action`
          : `/app/crm/shipment/${row?.shipment?._id || row?.shipmentId
          }/detail?tab=action`;

        return displayId ? (
          <Link
            title={displayId}
            to={path}
            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            <HighlightedText text={displayId} highlight={keyword} />
          </Link>
        ) : (
          "N/A"
        );
      },
      minWidth: "200px",
    },
    {
      name: "Action Type",
      selector: (row) => row?.actionType,
      cell: (row) => row?.actionType || "N/A",
      minWidth: "150px",
    },
    {
      name: "Description",
      selector: (row) => row?.description,
      cell: (row) => (
        <div title={row?.description} className="line-clamp-2">
          {ReactHtmlParser(row?.description || "N/A")}
        </div>
      ),
      minWidth: "250px",
    },
    {
      name: "Responsible",
      selector: (row) => row?.responsible?.name,
      cell: (row) => row?.responsible?.name || "N/A",
      minWidth: "150px",
    },
    {
      name: "Vendor",
      selector: (row) => row?.vendor?.ownerName,
      cell: (row) => (
        <div title={row?.vendor?.ownerName} className="line-clamp-2">
          {row?.vendor?.ownerName ? (
            <HighlightedText
              text={row?.vendor?.ownerName}
              highlight={keyword}
            />
          ) : (
            "N/A"
          )}
        </div>
      ),
      minWidth: "150px",
    },
    {
      name: "System Type",
      selector: (row) => row?.systemType,
      cell: (row) => row?.systemType || "N/A",
      minWidth: "120px",
    },
    {
      name: "Status",
      selector: (row) => row?.status,
      cell: (row) => (
        <Chip
          label={row?.status || "N/A"}
          color={
            row?.status === "Open"
              ? "warning"
              : row?.status === "In Progress"
                ? "info"
                : row?.status === "Read"
                  ? "primary"
                  : row?.status === "Closed"
                    ? "success"
                    : "default"
          }
          title={row?.status || "N/A"}
          size="small"
          sx={{
            minWidth: "80px",
            height: 20,
            fontSize: "0.7rem",
            borderRadius: "25px",
          }}
        />
      ),
      minWidth: "120px",
    },
    {
      name: "Created At",
      selector: (row) => <AppTableDateTime date={row?.createdAt} />,
    },
    {
      name: "Responses",
      selector: (row) => row?.responses?.length || 0,
      cell: (row) => row?.responses?.length || "0",
      minWidth: "100px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link
            to={
              isVendor
                ? `/app/vendor/shipment/${row?.shipment?._id || row?.shipmentId
                }/detail?tab=action`
                : `/app/crm/shipment/${row?.shipment?._id || row?.shipmentId
                }/detail?tab=action`
            }
          >
            <IconButton size="small" aria-label="View Shipment Action">
              <InfoOutlined color="primary" />
            </IconButton>
          </Link>
        </>
      ),
    },
  ];

  const [triggerActions, result] = useLazyGetFilteredShipmentActionsQuery();
  const {
    data: actionsData,
    isLoading: loadingActions,
    isFetching: fetchingActions,
    error,
  } = result;

  // Fetch users and vendors
  const [fetchUsers, { data: usersData, isLoading: loadingUsers }] =
    useLazyGetDDLUserListQuery();
  const [fetchVendors, { data: vendorsData, isLoading: loadingVendors }] =
    useLazyGetDdlVendorProfileQuery();
  const [fetchBranches, { isLoading: loadingBranches }] =
    useLazyGetDDLBranchListQuery();

  useEffect(() => {
    if (!viewEntity?.entity?._id) {
      console.error("No entity ID available for fetching shipment actions");
      return;
    }

    console.log("Fetching with entity ID:", viewEntity.entity._id); // Debugging

    fetchUsers({
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
    });
    fetchVendors({
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
    });
    fetchBranches({
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
    });
  }, [
    viewEntity?.entity?.account?._id,
    viewEntity?.entity?._id,
    fetchUsers,
    fetchVendors,
    fetchBranches,
  ]);

  useEffect(() => {
    if (!viewEntity?.entity?._id) {
      console.error("No entity ID available for fetching shipment actions");
      return;
    }

    console.log("Triggering shipment actions fetch with params:", {
      entity: viewEntity.entity._id,
      status: activeTab,
      keywords: keyword,
      page: currentPage,
      limit: perPage,
      user,
      vendor,
      systemType,
    }); // Debugging

    triggerActions({
      entity: viewEntity.entity._id,
      status: activeTab,
      keywords: keyword,
      page: currentPage,
      limit: perPage,
      ...(user && { user }),
      ...(vendor && { vendor }),
      ...(systemType && { systemType }),
    });
  }, [
    triggerActions,
    activeTab,
    keyword,
    currentPage,
    perPage,
    user,
    vendor,
    systemType,
    viewEntity?.entity?._id,
  ]);

  useAppMeta(`${activeTab} Shipment Actions`);

  // Prepare options for user, vendor, and systemType dropdowns
  const userOptions = React.useMemo(() => {
    const validUsers = Array.isArray(usersData)
      ? usersData.map((item) => ({
        label: item?.name,
        value: item?._id,
      }))
      : [];
    return [{ label: "All Users", value: "" }, ...validUsers];
  }, [usersData]);

  const vendorOptions = React.useMemo(() => {
    const validVendors = Array.isArray(vendorsData)
      ? vendorsData.map((item) => ({
        label: item?.ownerName,
        value: item?._id,
      }))
      : [];
    return [{ label: "All Vendors", value: "" }, ...validVendors];
  }, [vendorsData]);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error
        error={
          error ||
          (!viewEntity?.entity?._id
            ? {
              message: "No entity ID available for fetching shipment actions",
            }
            : null)
        }
      />
      <BreadcrumbLayout.Paper>
        <BaseTabs tabs={ActionListTabs} activeTab={activeTab} />
        <TableFilter searchLabel="Shipment ID" initialFilters={3}>
          <BaseAutocomplete
            fullWidth
            label="Responsible"
            value={userOptions.find((opt) => opt.value === user) || null}
            options={userOptions}
            onChange={(_, selected) => {
              setUser(selected?.value ?? "");
            }}
            getOptionLabel={(opt) => opt?.label || "Unknown User"}
            isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
            disabled={loadingUsers}
          />
          <BaseAutocomplete
            fullWidth
            label="Vendor"
            value={vendorOptions.find((opt) => opt.value === vendor) || null}
            options={vendorOptions}
            onChange={(_, selected) => {
              setVendor(selected?.value ?? "");
            }}
            getOptionLabel={(opt) => opt?.label || "Unknown Vendor"}
            isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
            disabled={loadingVendors}
          />
          <BaseAutocomplete
            fullWidth
            label="System Type"
            value={
              systemTypeOptions.find((opt) => opt.value === systemType) || null
            }
            options={systemTypeOptions}
            onChange={(_, selected) => {
              setSystemType(selected?.value ?? "");
            }}
            getOptionLabel={(opt) => opt?.label || "Unknown System Type"}
            isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
            disabled={false}
          />
        </TableFilter>
        <AppTable
          columns={columns}
          data={actionsData?.data || []}
          paginationServer
          paginationPerPage={perPage}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          progressPending={
            loadingActions ||
            fetchingActions ||
            loadingUsers ||
            loadingVendors ||
            loadingBranches
          }
          paginationTotalRows={actionsData?.totalItems || 0}
          noDataComponent={
            <Typography>
              No shipment actions found for the given filters.
            </Typography>
          }
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default ShipmentActionFollowupList;

const PageActions = () => {
  const { isAdmin } = useAuth();
  return (
    <>
      <Link to="/app/crm/action-list/create">
        {isAdmin && (<Button startIcon={<Add />} variant="contained" color="primary">
          Add Bulk Action
        </Button>)}
      </Link>
    </>
  );
};

